import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, FileUpIcon, Loader2Icon, PaperclipIcon, XIcon } from 'lucide-react';
import { Select } from './Select';
import { contact } from '../data/contact';
type Status = 'idle' | 'sending' | 'sent';
type Fields = {
  name: string;
  company: string;
  reach: string;
  type: string;
  timeline: string;
  message: string;
};
type FieldKey = keyof Fields | 'consent';
const ACCEPT = '.step,.stp,.iges,.igs,.dxf,.dwg,.x_t,.x_b,.stl,.ipt,.iam,.pdf,.png,.jpg,.jpeg,.zip';
const MAX_MB = 50;
const projectTypes = ['3D-Konstruktion & Baugruppen', 'Technische Zeichnungen', 'Blech & Schweißbaugruppen', 'Reverse Engineering', 'Patent & Visualisierung', 'Konstruktionskapazität'];
const timelines = ['So schnell wie möglich', 'Innerhalb von 2 Wochen', 'Innerhalb eines Monats', 'Flexibel'];
const initialFields: Fields = {
  name: '',
  company: '',
  reach: '',
  type: projectTypes[0],
  timeline: timelines[1],
  message: ''
};
const cardShadow = 'shadow-[0_0_0_1px_rgba(70,28,64,0.07),0_1px_2px_rgba(70,28,64,0.05),0_10px_28px_-10px_rgba(70,28,64,0.12)]';
const inputBase = 'h-14 w-full rounded-[8px] bg-canvas px-4 text-[16px] text-ink transition-shadow duration-150 placeholder:text-muted/70 focus:outline-none';
const ring = (err: boolean) => err ? 'shadow-[inset_0_0_0_1px_#b42318]' : 'shadow-[inset_0_0_0_1px_rgba(70,28,64,0.06)] focus:shadow-[inset_0_0_0_1px_rgba(13,13,18,0.5)]';
export function ContactQuoteForm() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fields, setFields] = useState<Fields>(initialFields);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<{
    key: FieldKey | 'file';
    text: string;
  } | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const update = (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFields(f => ({
      ...f,
      [key]: e.target.value
    }));
    if (error) setError(null);
  };
  const setField = (key: keyof Fields) => (value: string) => {
    setFields(f => ({
      ...f,
      [key]: value
    }));
    if (error) setError(null);
  };
  const pickFile = (f: File | undefined) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      setError({
        key: 'file',
        text: `Datei ist größer als ${MAX_MB} MB – bitte als ZIP komprimieren.`
      });
      return;
    }
    setError(null);
    setFile(f);
  };
  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const reach = fields.reach.trim();
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reach);
    const isPhone = /^\+?[\d\s/()-]{7,}$/.test(reach);
    if (!fields.name.trim()) return setError({
      key: 'name',
      text: 'Bitte geben Sie Ihren Namen an.'
    });
    if (!isEmail && !isPhone) return setError({
      key: 'reach',
      text: 'Bitte geben Sie eine gültige E-Mail oder Telefonnummer an.'
    });
    if (fields.message.trim().length < 10 && !file) return setError({
      key: 'message',
      text: 'Bitte beschreiben Sie das Bauteil kurz oder laden Sie eine Datei hoch.'
    });
    if (!consent) return setError({
      key: 'consent',
      text: 'Bitte stimmen Sie der Datenschutzerklärung zu.'
    });
    setError(null);
    setStatus('sending');
    window.setTimeout(() => setStatus('sent'), 1200);
  };
  const reset = () => {
    setFields(initialFields);
    clearFile();
    setConsent(false);
    setStatus('idle');
  };
  return <div className="w-full max-w-[820px]" data-id="element-113" data-mp="17tm2aenfhkp9">
      <div className={`rounded-[12px] bg-white p-2 text-left md:p-3 ${cardShadow}`} data-id="element-114" data-mp="17unfaoo08h9m">
        <AnimatePresence mode="wait" initial={false} data-id="element-115" data-mp="tvnn571a9k8p">
          {status === 'sent' ? <motion.div key="sent" role="status" initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} className="flex flex-col items-center px-6 py-14 text-center" data-id="element-116" data-mp="yse37ud38fu4">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-ink text-white" data-id="element-117" data-mp="1nfoxw2t6zemx">
                <CheckIcon className="h-5 w-5" data-id="element-118" data-mp="1ig41o05epkdw" />
              </span>
              <p className="mt-5 text-[20px] font-medium text-ink" data-id="element-119" data-mp="1ie9f6hehlk7p">Anfrage erhalten.</p>
              <p className="mt-2 max-w-[420px] text-[15px] text-muted" data-id="element-120" data-mp="1dfy2lywt4vls">
                Danke, {fields.name.trim().split(' ')[0]}. Ihr kostenloses Angebot kommt innerhalb eines Werktags an{' '}
                {fields.reach}.
              </p>
              <button type="button" onClick={reset} className="mt-6 text-[14px] text-muted underline underline-offset-4 hover:text-ink" data-id="element-121" data-mp="33tf87rawmz50">
                Neue Anfrage
              </button>
            </motion.div> : <motion.form key="form" onSubmit={onSubmit} noValidate initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} aria-label="Kostenloses Angebot anfordern" className="grid gap-2 md:grid-cols-2" data-id="element-122" data-mp="36d3cesdndt8w">
              <label className="sr-only" htmlFor="cq-name" data-id="element-123" data-mp="2ngan177lpcju">Name</label>
              <input id="cq-name" value={fields.name} onChange={update('name')} autoComplete="name" placeholder="Name" className={`${inputBase} ${ring(error?.key === 'name')}`} data-id="element-124" data-mp="2se3pbp5s9meu" />

              <label className="sr-only" htmlFor="cq-company" data-id="element-125" data-mp="2xft2backga5k">Unternehmen (optional)</label>
              <input id="cq-company" value={fields.company} onChange={update('company')} autoComplete="organization" placeholder="Unternehmen (optional)" className={`${inputBase} ${ring(false)}`} data-id="element-126" data-mp="31cxmbgn10esx" />

              <label className="sr-only" htmlFor="cq-reach" data-id="element-127" data-mp="gqvxhssk6xa7">E-Mail oder Telefonnummer</label>
              <input id="cq-reach" type="text" inputMode="email" autoComplete="email" value={fields.reach} onChange={update('reach')} placeholder="E-Mail oder Telefonnummer" className={`${inputBase} md:col-span-2 ${ring(error?.key === 'reach')}`} data-id="element-128" data-mp="2drjrgvgnjt8t" />

              <Select label="Leistung" value={fields.type} options={projectTypes} onChange={setField('type')} data-id="element-129" data-mp="1u1pr3szvg5sz" />
              <Select label="Zeitrahmen" value={fields.timeline} options={timelines} onChange={setField('timeline')} data-id="element-130" data-mp="1pxrvhs9r9d4o" />

              <label className="sr-only" htmlFor="cq-message" data-id="element-131" data-mp="1z0j4llf5rwt5">Projektbeschreibung</label>
              <textarea id="cq-message" rows={4} value={fields.message} onChange={update('message')} placeholder="Was soll konstruiert werden? Bauteil, Werkstoff, Stückzahl, vorhandene Daten …" className={`${inputBase} h-auto resize-none py-3.5 md:col-span-2 ${ring(error?.key === 'message')}`} data-id="element-132" data-mp="1yyq1gg3r7upw" />

              <input ref={fileRef} id="cq-file" type="file" accept={ACCEPT} className="sr-only" onChange={e => pickFile(e.target.files?.[0])} data-id="element-133" data-mp="38yiuzi6x6cxs" />
              {file ? <div className="flex h-14 min-w-0 items-center gap-2.5 rounded-[8px] bg-canvas pl-4 pr-1.5" data-id="element-134" data-mp="3dvj461z2fvt4">
                  <FileUpIcon className="h-[18px] w-[18px] shrink-0 text-ink" data-id="element-135" data-mp="5fzfzhd953kx" />
                  <div className="min-w-0 flex-1" data-id="element-136" data-mp="9imbbs94t6mv">
                    <p className="truncate text-[15px] font-medium leading-tight text-ink" data-id="element-137" data-mp="3rpjvianougjg">{file.name}</p>
                    <p className="text-[12px] leading-tight text-muted" data-id="element-138" data-mp="hxy2zrkbab1">{formatSize(file.size)}</p>
                  </div>
                  <button type="button" onClick={clearFile} aria-label="Datei entfernen" className="grid h-9 w-9 shrink-0 place-items-center rounded-[6px] text-muted transition-colors duration-150 hover:bg-white hover:text-ink" data-id="element-139" data-mp="2g93pf7y0xc99">
                    <XIcon className="h-4 w-4" data-id="element-140" data-mp="37zj4k60sdjro" />
                  </button>
                </div> : <label htmlFor="cq-file" onDragOver={e => {
            e.preventDefault();
            setDragging(true);
          }} onDragLeave={() => setDragging(false)} onDrop={e => {
            e.preventDefault();
            setDragging(false);
            pickFile(e.dataTransfer.files?.[0]);
          }} className={`flex h-14 cursor-pointer items-center gap-2.5 whitespace-nowrap rounded-[8px] border border-dashed px-4 transition-colors duration-150 ${dragging ? 'border-ink bg-canvas' : 'border-tint/40 hover:border-tint hover:bg-canvas'}`} data-id="element-141" data-mp="2xza42fzrhc6h">
                  <PaperclipIcon className="h-[18px] w-[18px] shrink-0 text-ink" strokeWidth={1.75} data-id="element-142" data-mp="lrmrfm95s8ni" />
                  <span className="text-[15px] font-medium text-ink" data-id="element-143" data-mp="prx5znb0o7dz">Projektdatei hochladen</span>
                  <span className="ml-auto hidden text-[13px] text-muted sm:inline" data-id="element-144" data-mp="upf4skvbl689">optional</span>
                </label>}

              <button type="submit" disabled={status === 'sending'} className="group inline-flex h-14 items-center justify-center gap-2 whitespace-nowrap rounded-[8px] bg-ink px-6 text-[16px] font-medium text-white transition-colors duration-150 hover:bg-ink/85 disabled:cursor-wait disabled:opacity-80" data-id="element-145" data-mp="14owcf7u4tu6v">
                {status === 'sending' ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-146" data-mp="1nggnux24vlx2" />
                    Wird gesendet
                  </> : <>
                    Kostenloses Angebot erhalten
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" data-id="element-147" data-mp="3lwizzic22ga3" />
                  </>}
              </button>
            </motion.form>}
        </AnimatePresence>
      </div>

      {status !== 'sent' && <div className="flex justify-center px-2 pt-3.5" data-id="element-148" data-mp="3hqk6biumlip">
          <label htmlFor="cq-consent" className="flex cursor-pointer items-center gap-2.5 text-left" data-id="element-149" data-mp="8gbj87md6z5k">
            <input id="cq-consent" type="checkbox" checked={consent} onChange={e => {
          setConsent(e.target.checked);
          if (error) setError(null);
        }} aria-required="true" className="peer sr-only" data-id="element-150" data-mp="demvep04tlqy" />
            <span aria-hidden="true" className={`grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[4px] transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-ink/30 peer-focus-visible:ring-offset-2 ${consent ? 'bg-ink text-white' : 'bg-white shadow-[inset_0_0_0_1px_rgba(13,13,18,0.25)]'}`} data-id="element-151" data-mp="2j9nv0w5muuqu">
              {consent && <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-152" data-mp="26y4ytvrb6c3q" />}
            </span>
            <span className="text-[13px] leading-[1.5] text-muted" data-id="element-153" data-mp="2203k1n0x0yy2">
              Ich akzeptiere die{' '}
              <a href={contact.privacyUrl} target="_blank" rel="noopener noreferrer" className="text-ink underline underline-offset-2 transition-colors duration-150 hover:text-ink/70" data-id="element-154" data-mp="2zm0vj0gvo40i">
                Datenschutzerklärung
              </a>
              .
            </span>
          </label>
        </div>}

      <div className="flex min-h-[32px] items-center justify-center px-2 pt-2 text-center" data-id="element-155" data-mp="1e9zhlizgr58n">
        {error ? <p role="alert" className="text-[13px] text-[#b42318]" data-id="element-156" data-mp="1a6b7asjg5vjp">
            {error.text}
          </p> : <p className="text-[13px] text-muted" data-id="element-157" data-mp="1bmxe5ijcsi1j">
            Kostenlos & unverbindlich · Angebot innerhalb 1 Werktag · NDA auf Wunsch · Projektpakete ab 400 €
          </p>}
      </div>
    </div>;
}
function formatSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}